// Angular
import { Component, OnInit, OnDestroy, inject, computed, signal, ElementRef, NgZone, effect, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators, AbstractControl, FormGroup } from '@angular/forms';
import { DatePipe } from '@angular/common';
// Services
import { ChatPanelService } from '../chat-panel.service';
import { FMSWebSocketService } from '../fms-web-socket.service';
// Lodash & RxJS
import { get } from 'lodash';
import { Subject, Subscription, takeUntil } from 'rxjs';
// Store
import { Store, select } from '@ngrx/store';
import { currentUser } from '@app/core/auth';
import { AppState } from '@app/core/auth/auth.store';
// Transloco
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-chat-user-list',
  templateUrl: './chat-user-list.component.html',
  styleUrls: ['./chat-user-list.component.scss'],
  host: { ngSkipHydration: 'true' },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ReactiveFormsModule, DatePipe, TranslocoDirective],
})
export class ChatUserListComponent implements OnInit, OnDestroy {
  /* ============================================================
   * DI (no constructor)
   * ============================================================ */
  public readonly svc = inject(ChatPanelService);
  public readonly fb = inject(FormBuilder);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly ws = inject(FMSWebSocketService);
  private readonly zone = inject(NgZone);
  private readonly translocoService = inject(TranslocoService);

  /* ============================================================
   * Public UI state
   * ============================================================ */

  /** Gate form (customer identification) */
  customerForm!: FormGroup;

  /** True once the customer submits the gate form */
  readonly chatStarted = signal(false);

  /** WebSocket connection health (surface as needed) */
  readonly connectionError = signal(false);

  /** Draft message in the composer */
  readonly message = signal('');

  /** Header title: company or selected rep name */
  readonly headerTitle = computed(() =>
    this.svc.isChatView()
      ? (this.svc.selectedUser()?.name ?? this.svc.companyName())
      : this.svc.companyName(),
  );

  /* ============================================================
   * Typing (customer → seller)
   * ============================================================ */

  private typingTimer: ReturnType<typeof setTimeout> | null = null;
  private typingActive = false;

  /* ============================================================
   * View refs & autoscroll
   * ============================================================ */

  /** Scrollable messages container */
  readonly messagesEl = viewChild<ElementRef<HTMLDivElement>>('messagesEl');

  /* ============================================================
   * Subscriptions / effects housekeeping
   * ============================================================ */

  /** Store subscriptions cleanup */
  private readonly unsubscribe$ = new Subject<void>();

  /** Single WS subscription kept stable across reconnects */
  private framesSub: Subscription | null = null;

  /* ============================================================
   * Lifecycle
   * ============================================================ */

  ngOnInit(): void {
    this.customerForm = this.fb.group({
      name: [null, [Validators.required, Validators.minLength(2)]],
    });
    this.store.pipe(select(currentUser),
      takeUntil(this.unsubscribe$)
    ).subscribe((user) => {
      const name = get(user, 'user.name', null);
      if (name) {
        this.customerForm.patchValue({ name }, { emitEvent: false });
      }
    });
  }

  ngOnDestroy(): void {
    // tear down store
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }
    if (this.framesSub) {
      this.framesSub.unsubscribe();
      this.framesSub = null;
    }
    // ensure socket is closed if panel was left open on destroy
    this.ws.disconnect();
  }

  /* ============================================================
   * WS lifecycle tied to panel visibility
   * - open() true  => ensure single subscription + connected
   * - open() false => unsubscribe + disconnect
   * ============================================================ */
  private readonly _wsLifecycleEff = effect(() => {
    const shouldConnect = this.svc.open(); // true when full or minimized

    if (shouldConnect) {
      if (!this.framesSub) {
        this.framesSub = this.ws.connect().subscribe({
          next: (frame: any) => {
            this.connectionError.set(false);
            if (frame && typeof frame === 'object' && 'r' in frame) {
              this.handleIncoming(frame);
            }
          },
          error: () => this.connectionError.set(true),
        });
      }
    } else {
      // panel closed → drop sub & close socket
      if (this.framesSub) {
        this.framesSub.unsubscribe();
        this.framesSub = null;
      }
      this.ws.disconnect();
    }
  });

  /* ============================================================
   * Effects (autoscroll & unread clear)
   * ============================================================ */

  /** Auto-scroll when messages / status / gate state change while chat view is visible */
  private readonly _autoScrollEff = effect(() => {
    const _len = this.svc.messages().length;
    const _status = this.svc.requestStatus();
    const _started = this.chatStarted();
    if (!this.svc.isChatView()) return;
    this.scrollToBottom();
  });

  /** Clear unread when the full panel is visible and we’re on the chat view */
  private readonly _clearUnreadEff = effect(() => {
    if (this.svc.isFullPanelVisible() && this.svc.isChatView()) {
      this.svc.clearUnread();
    }
  });

  /* ============================================================
   * Template helpers
   * ============================================================ */

  touched(ctrl: string): boolean {
    return (this.customerForm.get(ctrl) as AbstractControl)?.touched ?? false;
  }

  invalid(ctrl: string): boolean {
    return (this.customerForm.get(ctrl) as AbstractControl)?.invalid ?? false;
  }

  /* ============================================================
   * Navigation (Back/Close) + session control
   * ============================================================ */

  onBack(): void {
    if (!this.confirmEndIfNeeded()) return;
    this.endSession({ reconnect: true }); // keep panel open → list view wants a fresh session
    this.svc.backToList();
  }

  onClose(): void {
    if (!this.confirmEndIfNeeded()) return;
    this.endSession({ reconnect: false }); // panel closes → fully disconnect
    this.svc.closePanel();
  }

  /**
   * Confirm ending the chat only when it’s pending/accepted.
   * Declined/ended → no prompt. Idle with no messages → no prompt.
   */
  private confirmEndIfNeeded(): boolean {
    const status = this.svc.requestStatus();

    // No prompt on declined/ended (user already sees outcome)
    if (status === 'declined' || status === 'ended') return true;

    // Active handshake → confirm
    if (status === 'pending' || status === 'accepted') {
      return window.confirm(
        this.translocoService.translate('sentence_end_current_chat'),
      );
    }

    // Idle: if messages exist you may confirm; otherwise skip
    if (this.svc.messages().length === 0) return true;
    return window.confirm(
      this.translocoService.translate('sentence_close_chat'),
    );
  }

  /** Open chat with a representative; on switch, force a fresh session (legacy backend) */
  openChatWith(u: Parameters<ChatPanelService['selectUser']>[0]): void {
    if (!this.svc.canChat(u)) return;

    // Switching reps while anything existed → hard-reset socket session
    if (this.svc.selectedUser() && this.svc.selectedUser()!.id !== u.id) {
      this.endSession({ reconnect: true });
    }

    this.chatStarted.set(false);
    this.svc.selectUser(u);
  }

  /** Common end-of-chat path (notify + reset + network policy) */
  private endSession(opts: { reconnect: boolean }): void {
    const repId = this.svc.selectedUser()?.id;
    if (repId) {
      // tell backend we’re leaving this conversation
      this.ws.sendMessage({ f: 6, d: { t: { 1: repId }, s: 2 } });
    }

    // reset local UI/chat state
    this.chatStarted.set(false);
    this.svc.setRepTyping(false);
    this.svc.setRequestStatus('idle');
    this.svc.clearMessages();
    this.svc.clearUnread();

    // network policy
    if (opts.reconnect) {
      this.ws.reconnect(); // panel still open → keep list/presence fresh
    } else {
      this.ws.disconnect(); // full teardown (effect will also remain disconnected)
    }
  }

  /* ============================================================
   * Chat actions (start, send, typing)
   * ============================================================ */

  /** Submit gate → begin handshake (pending) and send initial contact frame */
  startChat(): void {
    if (this.customerForm.invalid) {
      this.customerForm.markAllAsTouched();
      return;
    }

    const repId = this.svc.selectedUser()?.id;
    const companyId = this.svc.company()?.id;
    if (!repId || !companyId) return;

    this.svc.beginChatRequest();
    this.svc.appendSystemMessage(
      this.translocoService.translate('sentence_request_sent'),
    );

    this.ws.sendMessage({
      f: 5,
      d: {
        t: { 1: repId },
        name: this.customerForm.get('name')?.value,
        reg: null,
        company_id: companyId,
        part_item: (this.svc as any)?.company()?.part_item ?? null, // keep legacy payload shape if present
        cart_key: null,
        order_id: null,
        zip: null,
        msg: '****** Incoming chat *******',
      },
    });

    this.chatStarted.set(true);
    this.scrollToBottom();

    // GA event
    this.sendGAEvent('fms_chat_started');
  }

  /** Send a chat message (optimistic append) */
  send(ev: Event): void {
    ev.preventDefault();

    const text = this.message().trim();
    if (!text || !this.svc.composerEnabled()) return;

    const repId = this.svc.selectedUser()?.id;
    if (!repId) return;

    // Optimistic append + send
    this.svc.appendCustomerMessage(text);
    this.ws.sendMessage({ f: 2, d: { t: { 1: repId }, m: text } });
    this.message.set('');
    this.scrollToBottom();

    // Stop typing immediately after send
    this.sendTypingStop(repId);
  }

  /* ----- Typing (customer) ----- */

  onComposerFocus(): void {
    const repId = this.svc.selectedUser()?.id;
    if (repId && this.svc.composerEnabled()) this.sendTypingStart(repId);
  }

  onComposerBlur(): void {
    const repId = this.svc.selectedUser()?.id;
    if (repId && this.typingActive) this.sendTypingStop(repId);
  }

  onTypingInput(): void {
    const repId = this.svc.selectedUser()?.id;
    if (!repId || !this.svc.composerEnabled()) return;

    this.sendTypingStart(repId);
    // Debounce stop
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }
    this.typingTimer = setTimeout(() => this.sendTypingStop(repId), 1200);
  }

  private sendTypingStart(repId: unknown): void {
    if (this.typingActive) return;
    this.ws.sendMessage({ f: 4, d: { t: { 1: repId }, a: 1 } });
    this.typingActive = true;
  }

  private sendTypingStop(repId: unknown): void {
    this.ws.sendMessage({ f: 4, d: { t: { 1: repId }, a: 0 } });
    this.typingActive = false;
  }

  /* ============================================================
   * Incoming frames (server → client)
   * ============================================================ */

  private handleIncoming(msg: any): void {
    switch (Number(msg?.r)) {
      case 1: {
        // STATUS update from server
        const uid = msg?.d?.i; // seller user id
        const statusCode = Number(msg?.d?.s); // 1=online,2=away,3=busy,4=offline
        if (uid != null) this.svc.applyServerStatus(uid, statusCode);
        break;
      }

      case 2: {
        // Seller text message
        const text = String(msg?.d?.m ?? '');
        if (text) {
          this.svc.appendRepMessage(text);

          // If panel not fully visible, bump unread + ping
          const notVisible = !this.svc.isFullPanelVisible();
          if (notVisible) {
            this.svc.bumpUnread();
          }

          this.scrollToBottom();
        }
        break;
      }

      case 4: {
        // Typing / read status
        const a = Number(msg?.d?.a);
        if (a === 1) this.svc.setRepTyping(true);
        if (a === 0 || a === 3) this.svc.setRepTyping(false);
        break;
      }

      case 6: {
        // Accept / decline / end
        const s = Number(msg?.d?.s);

        if (s === 1) {
          // ACCEPTED → unlock composer immediately
          this.svc.setRequestStatus('accepted');
          this.svc.setRepTyping(false);
          this.svc.appendSystemMessage(
            this.translocoService.translate('dynamic_rep_accepted_request', {
              value: this.svc.selectedUser()?.name,
            }),
          );
          // GA event
          this.sendGAEvent('fms_chat_accepted');
        } else if (s === 0) {
          // DECLINED → stay in view, show system line, disable composer
          this.svc.setRequestStatus('declined');
          this.svc.setRepTyping(false);
          this.svc.appendSystemMessage(
            this.translocoService.translate('dynamic_rep_declined_request', {
              value: this.svc.selectedUser()?.name,
            }),
          );
          // GA event
          this.sendGAEvent('fms_chat_declined');
        } else if (s === 2) {
          // ENDED by seller → stay until user presses Back
          this.svc.setRequestStatus('ended');
          this.svc.setRepTyping(false);
          this.message.set('');
          this.svc.appendSystemMessage(
            this.translocoService.translate('dynamic_rep_chat_end', {
              value: this.svc.selectedUser()?.name,
            }),
          );
          // GA event
          this.sendGAEvent('fms_chat_ended');
        }

        this.scrollToBottom();
        break;
      }
    }
  }

  /* ============================================================
   * DOM helpers
   * ============================================================ */

  /**
   * Scroll the messages container to the bottom.
   * Uses double RAF for layout settle; runs outside Angular for smoothness.
   */
  private scrollToBottom(): void {
    const el = this.messagesEl()?.nativeElement;
    if (!el) return;

    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight;
        });
      });
    });
  }

  /* ============================================================
   * Google Analytics
   * ============================================================ */

  /**
   * Send event to Google Analytics via gtag
   * @private
   * @param {string} event_name
   * @returns {void}
   * @memberof ChatUserListComponent
   */
  private sendGAEvent(event_name: string): void {
    try {
      if (!window.dataLayer) return;
      gtag('event', event_name, {
        companyname: this.svc.company()?.name ?? '',
        companyid: this.svc.company()?.id ?? '',
        repid: this.svc.selectedUser()?.id ?? '',
        repname: this.svc.selectedUser()?.name ?? '',
      });
    } catch (error) { }
  }
}
